/* eslint-disable @typescript-eslint/no-explicit-any */

type QuerySchema = Record<string, object | ((...args: any[]) => object)>;

type MutationSchema = Record<string, object | ((...args: any[]) => object)>;

type NamespacedQuery<TSchema, TKey extends string> = {
  [P in keyof TSchema]: TSchema[P] & { queryKey: [TKey, P] };
} & { all: { queryKey: [TKey] } };

type NamespacedMutation<TSchema, TKey extends string> = {
  [P in keyof TSchema]: TSchema[P] & { mutationKey: [TKey, P] };
};

function transformSchema<
  TKey extends string,
  TSchema extends Record<string, any>,
  TReturnKey extends 'queryKey' | 'mutationKey',
> (globalKey: TKey, schema: TSchema, keyName: TReturnKey): Record<keyof TSchema, any> {
  const result: Record<string, any> = {};

  for (const key of Object.keys(schema) as (keyof TSchema)[]) {
    const entry = schema[key];
    const baseKey = [ globalKey, key ];

    if (typeof entry === 'function') {
      const resultCallback = (...args: any[]): TSchema[keyof TSchema] => {
        const options = entry(...args);

        return {
          ...options,
          [keyName]: [ ...baseKey, ...(options?.[keyName] ?? []) ],
        };
      };

      (resultCallback as any)[keyName] = baseKey;
      result[key as string] = resultCallback;
    } else {
      result[key as string] = {
        ...entry,
        [keyName]: [ ...baseKey, ...(entry?.[keyName] ?? []) ],
      };
    }
  }

  return result as Record<keyof TSchema, any>;
}

/**
 * Creates a namespaced query schema that automatically prefixes all query keys with a global namespace.
 *
 * This function takes a global key and a schema of query options, then returns a new schema where
 * all query keys are prefixed with the global key. This helps organize and namespace related queries
 * to avoid key collisions and improve cache management.
 *
 * @template TKey - The string literal type for the global namespace key
 * @template TSchema - The schema object containing query options or functions that return query options
 *
 * @param globalKey - The global namespace key that will prefix all query keys
 * @param schema - An object containing query options or functions that return query options
 *
 * @returns A namespaced query schema where:
 *   - Each query key is prefixed with the global key: `[globalKey, queryName, ...existingKeys]`
 *   - Functions are preserved and their returned options get the namespaced keys
 *   - An `all` property is added with `queryKey: [globalKey]` for invalidating all queries in the namespace
 *
 * @example
 * ```typescript
 * const userQueries = queryNamespace('users', {
 *   list: {
 *     queryKey: ['list'],
 *     queryFn: () => fetchUsers()
 *   },
 *   detail: (id: string) => ({
 *     queryKey: ['detail', id],
 *     queryFn: () => fetchUser(id)
 *   })
 * });
 *
 * // Result:
 * // userQueries.list.queryKey = ['users', 'list']
 * // userQueries.detail('123').queryKey = ['users', 'detail', '123']
 * // userQueries.all.queryKey = ['users']
 * ```
 */
export function queryNamespace<TKey extends string, TSchema extends QuerySchema> (
  globalKey: TKey,
  schema: TSchema,
): NamespacedQuery<TSchema, TKey> {
  const transformed = transformSchema<TKey, TSchema, 'queryKey'>(globalKey, schema, 'queryKey');

  return {
    all: { queryKey: [ globalKey ] },
    ...transformed,
  } as NamespacedQuery<TSchema, TKey>;
}

/**
 * Creates a namespaced mutation schema that automatically prefixes all mutation keys with a global namespace.
 *
 * This function takes a global key and a schema of mutation options, then returns a new schema where
 * all mutation keys are prefixed with the global key. This helps organize and namespace related mutations
 * to avoid key collisions and improve cache management.
 *
 * @template TKey - The string literal type for the global namespace key
 * @template TSchema - The schema object containing mutation options or functions that return mutation options
 *
 * @param globalKey - The global namespace key that will prefix all mutation keys
 * @param schema - An object containing mutation options or functions that return mutation options
 *
 * @returns A namespaced mutation schema where:
 *   - Each mutation key is prefixed with the global key: `[globalKey, mutationName, ...existingKeys]`
 *   - Functions are preserved and their returned options get the namespaced keys
 *
 * @example
 * ```typescript
 * const userMutations = mutationNamespace('users', {
 *   create: {
 *     mutationKey: ['create'],
 *     mutationFn: (user: User) => createUser(user)
 *   },
 *   update: (id: string) => ({
 *     mutationKey: ['update', id],
 *     mutationFn: (user: User) => updateUser(id, user)
 *   })
 * });
 *
 * // Result:
 * // userMutations.create.mutationKey = ['users', 'create']
 * // userMutations.update('123').mutationKey = ['users', 'update', '123']
 * ```
 */
export function mutationNamespace<TKey extends string, TSchema extends MutationSchema> (
  globalKey: TKey,
  schema: TSchema,
): NamespacedMutation<TSchema, TKey> {
  return transformSchema<TKey, TSchema, 'mutationKey'>(globalKey, schema, 'mutationKey') as NamespacedMutation<
  TSchema,
  TKey
  >;
}
